import React, { useState } from 'react';
import ReactPlayer from 'react-player';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Play, Clock, Music, Calendar } from 'lucide-react';

// Local Song interface that matches the component's usage
interface Song {
  id: string;
  title: string;
  artist: string;
  duration?: string; // This component expects string format like "3:45"
  year?: number;
  genre?: string;
  albumCover?: string;
  album_cover_url?: string; // Database field name
  videoUrl?: string;
}

interface VideoSongCardProps {
  song: Song;
  isPlaying?: boolean;
  onPlay?: () => void;
  className?: string;
}

const VideoSongCard: React.FC<VideoSongCardProps> = ({ 
  song, 
  isPlaying = false, 
  onPlay,
  className = ""
}) => {
  const [isVideoLoading, setIsVideoLoading] = useState(true);
  const [hasVideoError, setHasVideoError] = useState(false);

  const handlePlay = () => {
    if (onPlay) {
      onPlay();
    }
  };

  const handleVideoReady = () => {
    setIsVideoLoading(false);
  };

  const handleVideoError = () => {
    setHasVideoError(true);
    setIsVideoLoading(false);
  };

  // Check if the song has a valid video URL
  const hasVideo = song.videoUrl && song.videoUrl.trim() !== '';
  
  // Get album cover from either field name
  const albumCover = song.albumCover || song.album_cover_url;

  return (
    <Card className={`group hover:shadow-lg transition-all duration-300 overflow-hidden ${className}`}>
      <CardContent className="p-0">
        {/* Video Section */}
        <div className="relative aspect-video bg-gray-900">
          {hasVideo && !hasVideoError ? (
            <>
              {isVideoLoading && (
                <div className="absolute inset-0 flex items-center justify-center bg-gray-800">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
                </div>
              )}
              <ReactPlayer
                url={song.videoUrl}
                width="100%"
                height="100%"
                playing={isPlaying}
                controls={true}
                light={!isPlaying} // Show thumbnail when not playing
                playIcon={
                  <div className="flex items-center justify-center w-16 h-16 bg-black bg-opacity-50 rounded-full">
                    <Play className="w-8 h-8 text-white ml-1" fill="white" />
                  </div>
                }
                onReady={handleVideoReady}
                onError={handleVideoError}
                onPlay={handlePlay}
                pip={true}
                config={{
                  youtube: {
                    playerVars: {
                      showinfo: 1,
                      modestbranding: 1,
                    }
                  }
                }}
              />
            </>
          ) : (
            // Fallback when no video or video error
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-linear-to-br from-gray-800 to-gray-900">
              {hasVideoError ? (
                <>
                  <div className="text-red-400 text-center mb-2">
                    <Music className="w-8 h-8 mx-auto mb-2" />
                    <p className="text-sm">Video unavailable</p>
                  </div>
                </>
              ) : (
                <>
                  <Music className="w-12 h-12 text-gray-400 mb-3" />
                  <p className="text-gray-400 text-sm">No video available</p>
                </>
              )}
              
              {/* Album cover fallback */}
              {albumCover && (
                <div className="absolute inset-0 opacity-20">
                  <img 
                    src={albumCover} 
                    alt={`${song.title} cover`}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
            </div>
          )}
        </div>

        {/* Song Information */}
        <div className="p-4">
          <div className="flex items-start justify-between mb-2">
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-lg text-gray-900 truncate">
                {song.title}
              </h3>
              <p className="text-gray-600 truncate">
                {song.artist}
              </p>
            </div>
          </div>

          {/* Metadata */}
          <div className="flex flex-wrap gap-2 mb-3">
            {song.duration && (
              <Badge variant="secondary" className="text-xs">
                <Clock className="w-3 h-3 mr-1" />
                {song.duration}
              </Badge>
            )}
            {song.year && (
              <Badge variant="secondary" className="text-xs">
                <Calendar className="w-3 h-3 mr-1" />
                {song.year}
              </Badge>
            )}
            {song.genre && (
              <Badge variant="outline" className="text-xs">
                {song.genre}
              </Badge>
            )}
          </div>

          {/* Album Cover (small) when there's a video */}
          {hasVideo && albumCover && (
            <div className="flex items-center gap-2 pt-2 border-t border-gray-100">
              <img 
                src={albumCover} 
                alt={`${song.title} cover`}
                className="w-8 h-8 rounded object-cover border border-gray-200"
              />
              <span className="text-xs text-gray-500">Album artwork</span>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default VideoSongCard; 